import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import IndividualValidationResult, { buildDescription } from 'components/IndividualValidationResult';
import FormWorkflowStages from 'common/Workflows/FormWorkflowStages';
import FormTypes from 'common/Forms/FormTypes';
import {
  saveDraftValidationOverrides,
  acceptValidationOverrides,
  getStorageSetting
} from 'actions';
import OverrideReasonDropdown from './OverrideReasonDropdown';

class IndividualValidationOverridesSection extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    workflowStage: PropTypes.number,
    workflowUsername: PropTypes.string,
    valid: PropTypes.bool.isRequired,
    rejections: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      codeDescription: PropTypes.string
    })).isRequired,
    warnings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    overrides: PropTypes.shape({
      current: PropTypes.shape({}),
      draft: PropTypes.shape({})
    }),
    formType: PropTypes.string.isRequired,
    formId: PropTypes.number.isRequired,
    accountId: PropTypes.number.isRequired,
    customerId: PropTypes.string,
    customerName: PropTypes.string,
    isOnlineForm: PropTypes.bool.isRequired,
    isSavingValidationOverrides: PropTypes.bool,
    isWithTreaty: PropTypes.bool,
    isLocked: PropTypes.bool,
    expiryDate: PropTypes.string,
    expiryReasonDesc: PropTypes.string,
    validationResult: PropTypes.shape(),
    piiValidationCodes: PropTypes.shape(),
    isPiiProtectionEnabled: PropTypes.bool,
    showTinMatching: PropTypes.bool,
    tinMatchResult: PropTypes.arrayOf(PropTypes.shape()),
    onCheckTin: PropTypes.func,
    tinMatchFormProps: PropTypes.shape(),
    isFormDirty: PropTypes.bool
  };

  static defaultProps = {
    permissions: null,
    workflowStage: 0,
    workflowUsername: null,
    customerId: '',
    customerName: '',
    overrides: null,
    isSavingValidationOverrides: false,
    isWithTreaty: false,
    isLocked: false,
    expiryDate: null,
    expiryReasonDesc: '',
    validationResult: null,
    piiValidationCodes: null,
    isPiiProtectionEnabled: false,
    showTinMatching: false,
    tinMatchResult: null,
    onCheckTin: null,
    tinMatchFormProps: null,
    isFormDirty: false
  };

  constructor(props) {
    super(props);

    const existingOverrides = props.overrides.draft ? props.overrides.draft : props.overrides.current;
    const formOverride = existingOverrides ? existingOverrides.formOverride : {
      override: false,
      valid: props.valid,
      comments: ''
    };
    let overrideEntries = [];
    if (props.rejections != null) {
      overrideEntries = props.rejections;
      overrideEntries.map((obj) => {
        if (obj != null) {
          const existingOverride = existingOverrides != null
            && existingOverrides.rejections != null && existingOverrides.rejections.filter(item => item.code === obj.code);

          obj.selected = !!(existingOverride != null && existingOverride[0] != null);
          obj.reason = existingOverride != null && existingOverride[0] != null ? existingOverride[0].reason : '';
          obj.comments = existingOverride != null && existingOverride[0] != null ? existingOverride[0].comments : '';
          return obj;
        }
        return null;
      });
    }

    this.onOverrideReasonChange = this.onOverrideReasonChange.bind(this);

    this.state = {
      formOverride,
      overrideEntries
    };
  }

  componentDidUpdate() {
    const { overrides } = this.props;
    const existingOverrides = overrides && (overrides.draft ? overrides.draft : overrides.current);

    const { validationResult } = this.props;
    const { rejections } = validationResult && validationResult.result && validationResult.result.length > 0 && validationResult.result[0];

    if (rejections) {
      this.onRejectionsUpdated(rejections, existingOverrides);
    }
  }

  onRejectionsUpdated(rejections, existingOverrides) {
    const { overrideEntries } = this.state;
    const newRejections = rejections.map((obj) => {
      if (obj != null) {
        const existingOverride = existingOverrides != null
          && existingOverrides.rejections != null && existingOverrides.rejections.filter(item => item.code === obj.code);

        if (typeof obj.selected === 'undefined') {
          obj.selected = !!(existingOverride != null && existingOverride[0] != null);
        }
        if (typeof obj.reason === 'undefined') {
          obj.reason = existingOverride != null && existingOverride[0] != null ? existingOverride[0].reason : '';
        }
        if (typeof obj.comments === 'undefined') {
          obj.comments = existingOverride != null && existingOverride[0] != null ? existingOverride[0].comments : '';
        }
        return obj;
      }
      return null;
    });

    const currentCodes = overrideEntries.map(override => (override && override.code) || null);
    let changeFound = false;

    const newOverrideEntries = newRejections.reduce((result, curr) => {
      if (curr && curr.code) {
        const idx = currentCodes.indexOf(curr.code);
        if (idx === -1) {
          result.push(curr);
          changeFound = true;
        } else {
          result.push(overrideEntries[idx]);
        }
      }

      return result;
    }, []);

    if (changeFound || newOverrideEntries.length !== currentCodes.length) {
      this.setState({ overrideEntries: newOverrideEntries });
    }
  }

  onFormOverrideSelected(e) {
    const { formOverride } = this.state;

    formOverride.override = e.target.checked;
    formOverride.valid = !e.target.checked;
    formOverride.comments = '';
    this.setState({
      formOverride
    });
  }

  onFormOverrideCommentChange(e) {
    const { formOverride } = this.state;

    formOverride.comments = e.target.value;
    this.setState({
      formOverride
    });
    e.preventDefault();
  }

  onOverrideSelected(e, code) {
    const { overrideEntries } = this.state;

    if (overrideEntries != null) {
      overrideEntries.filter(item => item.code === code).map((obj) => {
        if (obj != null) {
          obj.selected = e.target.checked;
          if (!e.target.checked) {
            obj.reason = '';
            obj.comments = '';
          }
          return obj;
        }
        return null;
      });

      this.setState({
        overrideEntries
      });
    }
  }

  onOverrideReasonChange(e, code) {
    const { overrideEntries } = this.state;
    if (overrideEntries != null) {
      overrideEntries.filter(item => item.code === code).map((obj) => {
        if (obj != null) {
          obj.reason = e.target.value;
          return obj;
        }
        return null;
      });

      this.setState({
        overrideEntries
      });
    }
    e.preventDefault();
  }

  onOverrideCommentChange(e, code) {
    const { overrideEntries } = this.state;

    if (overrideEntries != null) {
      overrideEntries.filter(item => item.code === code).map((obj) => {
        if (obj != null) {
          obj.comments = e.target.value;
          return obj;
        }
        return null;
      });

      this.setState({
        overrideEntries
      });
    }
    e.preventDefault();
  }

  onSaveOverrides() {
    const { dispatch, formId, accountId } = this.props;
    const { overrideEntries, formOverride } = this.state;

    const selectedEntries = overrideEntries != null ? overrideEntries.filter(item => item.selected) : null;

    dispatch(saveDraftValidationOverrides(formId, accountId, {
      formOverride: formOverride ? {
        isDraft: true,
        override: formOverride.override,
        valid: formOverride.valid,
        reason: '4',
        comments: formOverride.comments
      } : null,
      rejections: selectedEntries
    }));
  }

  onAcceptOverrides() {
    const { dispatch, formId, accountId } = this.props;
    const { overrideEntries, formOverride } = this.state;

    const selectedEntries = overrideEntries != null ? overrideEntries.filter(item => item.selected) : null;

    dispatch(acceptValidationOverrides(formId, accountId, {
      formOverride: formOverride ? {
        isDraft: false,
        override: formOverride.override,
        valid: formOverride.valid,
        reason: '4',
        comments: formOverride.comments
      } : null,
      rejections: selectedEntries
    }));
  }

  getOverriddenRejections() {
    const { overrideEntries, formOverride } = this.state;

    return overrideEntries != null && {
      current: {
        formOverride: formOverride ? {
          isDraft: true,
          override: formOverride.override,
          valid: formOverride.valid,
          reason: '4',
          comments: formOverride.comments
        } : null,
        rejections: overrideEntries.filter(item => item.selected)
      }
    };
  }

  canOverride() {
    const { isLocked, permissions, workflowStage, workflowUsername } = this.props;

    const isUserFormOwner = workflowUsername != null && workflowUsername === getStorageSetting('username');
    const hasReviewerPermission = AccessControl.hasPermission(permissions, [ActivityPerms.fwfInitialReviewer, ActivityPerms.fwfSecondaryReviewer, ActivityPerms.fwfExpertReviewer, ActivityPerms.fwfSupplReviewer]);
    const hasOverridePermission = AccessControl.hasPermission(permissions, [ActivityPerms.overrideValidation]);

    if (isLocked || workflowStage === FormWorkflowStages.Complete || workflowStage === FormWorkflowStages.BatchStaging) {
      // WF is Complete or Batch Staging
      return false;
    }

    return isUserFormOwner && hasReviewerPermission && hasOverridePermission;
  }

  isPiiValidationCode(validationCode) {
    const { formType, piiValidationCodes } = this.props;
    const formTypeFamily = formType && FormTypes[formType.toLowerCase()];
    const family = formTypeFamily && formTypeFamily.family && formTypeFamily.family.toUpperCase();

    const result = family && piiValidationCodes && piiValidationCodes[family]
    && piiValidationCodes[family].filter(code => code.substring(4) === validationCode.substring(3)).length !== 0;
    return result;
  }

  render() {
    const {
      isSavingValidationOverrides,
      formId,
      customerId,
      customerName,
      formType,
      isOnlineForm,
      valid,
      rejections,
      warnings,
      isWithTreaty,
      isLocked,
      workflowStage,
      expiryDate,
      expiryReasonDesc,
      piiValidationCodes,
      isPiiProtectionEnabled,
      showTinMatching,
      tinMatchResult,
      onCheckTin,
      tinMatchFormProps,
      isFormDirty
    } = this.props;

    const { overrideEntries, formOverride } = this.state;

    const overrideExceptedRejection = '500000';

    const overrideReasons = [
      { value: '1', label: 'Supporting document attached.' },
      { value: '2', label: 'Exception approval attached.' },
      { value: '3', label: 'System issue.' },
      { value: '4', label: 'Client specific treatment, specified below.' },
      { value: '5', label: 'Other reason, specified below.' },
      { value: '7', label: 'Alt. Withholding Statement has declaration within it.' },
    ];

    const sortOverrideReasons = (reasons) => {
      const sortedReasons = reasons.sort((a, b) => {
        if (a.label === 'Select a reason') return -1;
        if (b.label === 'Select a reason') return 1;
        if (a.label === 'Other reason, specified below.') return 1;
        if (b.label === 'Other reason, specified below.') return -1;

        return a.label.localeCompare(b.label);
      });

      return sortedReasons;
    };

    const piiOverrideReasons = sortOverrideReasons([{ value: '6', label: 'PII Data.' }, ...overrideReasons]);
    const defaultOverrideReasons = sortOverrideReasons([{ value: '', label: 'Select a reason' }, ...overrideReasons]);

    return (
      <div className="validation-overrides">
        <h1>Validation Overrides</h1>
        <div className="validation-overrides-content">
          <div className="overrides-entry">
            <div className="result-block">
              { !valid && <p>If you received or sourced information additional to this tax document that causes any of the rejections listed below to be incorrect, please take the following steps:</p> }
              { valid && <p>If you received or sourced information additional to this tax document that causes the validation result to be incorrect, please take the following steps:</p> }
              <ol>
                { !valid && <li>Please select an applicable reason,</li> }
                { valid && <li>Please check the check box next to the Invalidate form text below,</li> }
                <li>Please enter a short rationale to the text box below,</li>
                <li>Please upload any supporting document substantiating your notes,</li>
                <li>Please save your changes. </li>
              </ol>
              <p>Once you have finished with your edits please click the “Accept” button at the bottom of the page to apply your changes.</p>
              <div className="reasons-for-rejection-list">
                { (!rejections || rejections.length === 0) && formOverride && <div className="form-override">
                  <p><em>Invalidate this form.</em></p>
                  <label className="form-checkbox">
                    <input
                      id="form-override-cb"
                      type="checkbox"
                      value="Yes"
                      checked={ formOverride.override }
                      disabled={ !this.canOverride() }
                      onChange={ e => this.onFormOverrideSelected(e) }
                    />
                    <i className="form-icon" />
                  </label>
                  { formOverride && formOverride.override && <select
                    id="form=override-dd"
                    className="form-select"
                    disabled
                    value="4"
                  >
                    {defaultOverrideReasons.map(option => (
                      <option key={ option.value } value={ option.value }>
                        {option.label}
                      </option>
                    ))}
                  </select> }
                  { formOverride && formOverride.override && <textarea
                    id="form-override-ta"
                    className="form-input"
                    rows="3"
                    value={ formOverride.comments }
                    disabled={ !this.canOverride() }
                    onChange={ e => this.onFormOverrideCommentChange(e) }
                  /> }
                </div> }
                <ul>
                  {overrideEntries != null && overrideEntries.length !== 0 && overrideEntries.map(rejection => rejection != null && rejection.code.substring(3) !== overrideExceptedRejection
                    && !this.isPiiValidationCode(rejection.code) && (
                    <li key={ rejection.key }>
                      <p><em>{ buildDescription(rejection) }</em>
                      &nbsp;(<strong>{ rejection.code }</strong>)</p>
                      <label className="form-checkbox">
                        <input
                          id={ `override-cb-${ rejection.key }` }
                          type="checkbox"
                          value="Yes"
                          checked={ rejection.selected || false }
                          disabled={ !this.canOverride() }
                          onChange={ e => this.onOverrideSelected(e, rejection.code) }
                        />
                        <i className="form-icon" />
                      </label>
                      {rejection.selected && <OverrideReasonDropdown
                        onOverrideReasonChange={ this.onOverrideReasonChange }
                        rejection={ rejection }
                        options={ defaultOverrideReasons }
                        canOverride={ !this.canOverride() }
                        defaultValue=""
                      />}
                      { rejection.selected && <textarea
                        id={ `override-ta-${ rejection.key }` }
                        className="form-input"
                        rows="3"
                        maxLength="2000"
                        value={ rejection.comments }
                        disabled={ !this.canOverride() }
                        onChange={ e => this.onOverrideCommentChange(e, rejection.code) }
                      /> }
                    </li>
                  )) }
                </ul>
                {isPiiProtectionEnabled === true && overrideEntries != null
                  && overrideEntries.some(rejection => rejection != null && this.isPiiValidationCode(rejection.code))
                  && <p className="pii-title"><strong>Personally Identifiable Information (PII) Rejections:</strong></p>}
                <ul>
                  {isPiiProtectionEnabled === true && overrideEntries != null && overrideEntries.length !== 0 && overrideEntries.map(rejection => rejection != null && rejection.code.substring(3) !== overrideExceptedRejection
                    && this.isPiiValidationCode(rejection.code) && (
                      <li key={ rejection.key }>
                        <p><em>{buildDescription(rejection)}</em>
                          &nbsp;(<strong>{rejection.code}</strong>)</p>
                        <label className="form-checkbox">
                          <input
                            id={ `override-cb-${ rejection.key }` }
                            type="checkbox"
                            value="Yes"
                            checked={ rejection.selected || false }
                            disabled={ !this.canOverride() }
                            onChange={ e => this.onOverrideSelected(e, rejection.code) }
                          />
                          <i className="form-icon" />
                        </label>
                        {rejection.selected && <OverrideReasonDropdown
                          onOverrideReasonChange={ this.onOverrideReasonChange }
                          rejection={ rejection }
                          options={ piiOverrideReasons }
                          canOverride={ !this.canOverride() }
                          defaultValue="6"
                        />}
                        {rejection.selected && <textarea
                          id={ `override-ta-${ rejection.key }` }
                          className="form-input"
                          rows="3"
                          maxLength="2000"
                          value={ rejection.comments }
                          disabled={ !this.canOverride() }
                          onChange={ e => this.onOverrideCommentChange(e, rejection.code) }
                        />}
                      </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="form-group save-draft text-right">
              <div className="col-12">
                <button
                  id="save-overrides-btn"
                  type="button"
                  className={ `btn btn-primary ${ !this.canOverride() ? 'none' : '' }` }
                  disabled={ isSavingValidationOverrides }
                  onClick={ () => this.onSaveOverrides() }
                >
                  Save
                </button>
              </div>
            </div>
          </div>
          <div className="validation-result-content">
            <div className="preview-result validation-result-message">
              <h1>Validation Result Preview</h1>
              <IndividualValidationResult
                formId={ formId }
                customerId={ customerId }
                customerName={ customerName }
                formType={ formType }
                isOnlineForm={ isOnlineForm }
                valid={ valid }
                rejections={ rejections }
                warnings={ warnings }
                overrides={ this.getOverriddenRejections() }
                isWithTreaty={ isWithTreaty }
                isLocked={ isLocked }
                workflowStage={ workflowStage }
                expiryDate={ expiryDate }
                expiryReasonDesc={ expiryReasonDesc }
                calculateValidity
                piiValidationCodes={ piiValidationCodes }
                isPiiProtectionEnabled={ isPiiProtectionEnabled }
                showTinMatching={ showTinMatching }
                tinMatchResult={ tinMatchResult }
                onCheckTin={ onCheckTin }
                tinMatchFormProps={ tinMatchFormProps }
                isFormDirty={ isFormDirty }
              />
              <div className="form-group">
                <div className="col-12 text-right">
                  <button
                    id="accept-overrides-btn"
                    type="button"
                    className={ `btn btn-primary ${ !this.canOverride() ? 'none' : '' }` }
                    disabled={ isSavingValidationOverrides || isLocked }
                    onClick={ () => this.onAcceptOverrides() }
                  >
                    Accept
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    forms: {
      isSavingValidationOverrides,
      validationResult
    }
  } = state;

  return {
    isSavingValidationOverrides,
    validationResult
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

const IndividualValidationOverrides = connect(
  mapStateToProps,
  mapDispatchToProps
)(IndividualValidationOverridesSection);

export default IndividualValidationOverrides;
